import Icon from 'antd/lib/icon';
import * as React from 'react';
import useFieldGroup from 'src/hooks/form/useFieldGroup';
import useMultiField from "src/hooks/form/useMultiField";
import { useGenericState } from 'src/Library/UseGenericState';
import { DropdownMasterValues } from '../SEActions/SETasks/SETasks.interfaces';
import SEFieldInput from './SEFieldInput';
import SEFieldLabel from './SEFieldLabel';

const SEMultiFieldGroup = ({
  fieldGroup,
  field,
  showLabel = false,
  showDelete = false,
  showAdd = false,
  onSubmit,
  dropdownMasterValues,
  duplicateField,
  deleteField,
}: {
  dropdownMasterValues: DropdownMasterValues;
  onSubmit?: () => void;
  fieldGroup: ReturnType<typeof useFieldGroup>;
  duplicateField?: (field: any) => void;
  deleteField?: (field: any) => void;
  field: ReturnType<typeof useMultiField>;
  showLabel?: boolean;
  showDelete?: boolean;
  showAdd?: boolean;
}) => {
  let disabled = true;
  // isMultiFieldPresent = true;
  const isFieldObject = field?.defaultRenderProps?.extraData?.type === 'OBJECT';

  const initialState = {
    mutiValuesCollapsed: true,
  };
  const [state, setState] = useGenericState(initialState);
  const { mutiValuesCollapsed } = state;

  const asyncRerender = (cb: () => boolean) => {
    setTimeout(() => {
      if (cb()) {
        fieldGroup.requestRerender();
      } else {
        asyncRerender(cb);
      }
    }, 100);
  };

  const renderSubmit = (cb: any) => {
    return (
      <Icon
        type="check-circle"
        style={{
          cursor: 'pointer',
          color: '#38D600',
        }}
        onClick={cb}
      />
    );
  };

  const renderAdd = (cb: any) => {
    return (
      <div style={{ 
        cursor: 'pointer', 
        color: '#1890FF',
        marginLeft: 16,
      }
      } onClick={cb}>
        <Icon type="plus" />
          <span> Add More</span>
      </div>
    );
  };

  const renderDelete = (cb: any) => {
    return (
      <Icon
        type="close-circle"
        style={{ cursor: 'pointer', marginBottom: 2 }}
        theme="filled"
        onClick={cb}
      />
    );
  };

  const toggleCollapse = () => {
    setState({ mutiValuesCollapsed: !mutiValuesCollapsed });
  };

  const renderCollapsedValues = (elems: any[]) => {
    let visibleIndex = 0;
    return (
      <div style={{ cursor: 'pointer' }} onClick={toggleCollapse}>
        {elems.map((elem, idx) => {
          if (!elem) return null;
          const { Node, ref, nodeKey } = elem;
          const subField = ref?.current?.field;
          const number = ++visibleIndex;
          return (
            <div style={{ marginBottom: 2 }}>
              {subField ? (
                <Node key={nodeKey}>
                  <SEFieldInput
                    style={
                      idx === 0 || !mutiValuesCollapsed
                        ? {}
                        : {
                          display: 'none',
                        }
                    }
                    field={subField}
                    displayNumber={number}
                    dropdownMasterValues={dropdownMasterValues}
                  />
                </Node>
              ) : null}
              {idx === 0 && mutiValuesCollapsed && elems.filter(e => e !== null).length > 1 ? (
                <div
                  style={{
                    marginTop: 4,
                    color: '#1890FF',
                  }}
                >
                  <span>{`+${elems.filter(e => e !== null).length - 1} more`}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return field?.render((elems: any) => {
    let visibleIndex = 0;
    let lastIdx = elems.length - 1;
    /**
     * When a field is removed, the useMutliField hook leaves a null in the array 
     * instead of shrinking the array.
     * So, to find the last index which is not null/undefined, we loop from the end.
     * so that the reference is not pointed to a null value.
     * This helps in showing the add/delete buttons correctly.
     * as well as rendering the Submit button correctly.
     */
    while (lastIdx >= 0 && !elems[lastIdx]) {
      lastIdx -= 1;
    }
    const hasMultipleElements = elems.filter(e => e !== null).length > 1;
    return (
      <>
        {showLabel && (
          <SEFieldLabel
            bold={isFieldObject}
            label={field?.defaultRenderProps?.label}
            style={{ marginBottom: 8 }}
            isRequired={elems[lastIdx].ref?.current?.field?.isRequired}
            onEdit={onSubmit && (() => {
              elems.forEach((el) => el?.ref?.current?.field?.enable());
              asyncRerender(() => {
                return elems.reduce((isEnabled, el) => {
                  if (el?.ref?.current?.field?.disabled) {
                    return false;
                  }
                  return isEnabled;
                }, true);
              });
            })}
          />
        )}
        {elems.map((elem, idx) => {
          if (!elem) return null;
          const { Node, ref, nodeKey } = elem;
          const subField = ref?.current?.field;
          disabled = subField?.disabled;
          const number = ++visibleIndex;
          const isLastElement = idx === lastIdx;
          const shouldShowDelete = typeof showDelete === 'boolean' && (isFieldObject && deleteField) ? showDelete : hasMultipleElements;
          const shouldShowAdd = typeof showAdd === 'boolean' && (isFieldObject && duplicateField) ? showAdd : isLastElement;
          return (
            <>
              <div
                style={{
                  display: disabled ? 'flex' : 'grid',
                  gridTemplateColumns: '4fr 1fr',
                }}
              >
                <Node key={nodeKey}>
                  <SEFieldInput
                    style={{ display: disabled ? 'none' : 'block' }}
                    field={subField}
                    displayNumber={number}
                    dropdownMasterValues={dropdownMasterValues}
                  />
                </Node>
                {!field?.defaultRenderProps?.disabled && (
                  <div
                    style={{
                      marginLeft: 15,
                      display: 'flex',
                      alignItems: 'flex-end',
                      paddingBottom: 35,
                      height: '100%',
                    }}
                  >
                    {onSubmit && isLastElement &&
                      renderSubmit(onSubmit)
                    }
                    {shouldShowDelete
                      ? renderDelete(() => {
                        isFieldObject && deleteField
                          ? deleteField(ref?.current?.field)
                          : field.removeNode(idx);
                      })
                      : null}
                  </div>
                )}
              </div>
              {!field?.defaultRenderProps?.disabled && shouldShowAdd && (
                <div style={{ marginTop: -10 }}>
                  {renderAdd(() => {
                    isFieldObject && duplicateField
                      ? duplicateField(ref?.current?.field)
                      : field.addNode();
                  })}
                </div>
              )}
              <div />
            </>
          );
        })}
        {disabled && elems.length !== 0 && renderCollapsedValues(elems)}
        <div />
      </>
    );
  });
};

export default SEMultiFieldGroup;